import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  Paper,
  Typography,
} from '@mui/material';

const PatientInformation = () => {
  const [pendingCases, setPendingCases] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortConfig, setSortConfig] = useState({
    key: 'id',
    direction: 'asc',
  });

  useEffect(() => {
    fetchPendingCases();
  }, []);

  const fetchPendingCases = async () => {
    try {
      const { data: cases, error } = await supabase
        .from('visits')
        .select(
          'id, user_id, testdate, sorethroat, fever, headache, abdominalpain, difficultyswallowing, cough, congestion, rhinorrhea, hoarseness, conjunctivitis, knownrecentcontact, historyoftonsillectomy, antibioticsinlast48hours'
        )
        .eq('ispending', true);
      
      console.log('cases', cases);  
      if (error) throw error;
      setPendingCases(cases);
    } catch (error) {
      console.error('Error fetching pending cases:', error);
    }
  };

  const formatDate = (dateString) => {
    const options = {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
    };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const sortedCases = [...pendingCases].sort((a, b) => {
    if (a[sortConfig.key] < b[sortConfig.key]) {
      return sortConfig.direction === 'asc' ? -1 : 1;
    }
    if (a[sortConfig.key] > b[sortConfig.key]) {
      return sortConfig.direction === 'asc' ? 1 : -1;
    }
    return 0;
  });

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const handleChangePage = (event, newPage) => {
    setCurrentPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setCurrentPage(0);
  };

  const columnHeaderMapping = {
    id: 'ID',
    user_id: 'User ID',
    testdate: 'Test Date',
    sorethroat: 'Sore Throat',
    fever: 'Fever',
    headache: 'Headache',
    abdominalpain: 'Abdominal Pain',
    difficultyswallowing: 'Difficulty Swallowing',
    cough: 'Cough',
    congestion: 'Congestion',
    rhinorrhea: 'Rhinorrhea',
    hoarseness: 'Hoarseness',
    conjunctivitis: 'Conjunctivitis',
    knownrecentcontact: 'Known Recent Contact',
    historyoftonsillectomy: 'History of Tonsillectomy',
    antibioticsinlast48hours: 'Antibiotics in Last 48 Hours'
  };

  const renderTableData = () => {
    return sortedCases
      .slice(currentPage * rowsPerPage, currentPage * rowsPerPage + rowsPerPage)
      .map((caseItem) => (
        <TableRow
          key={caseItem.id}
          className="hover:bg-gray-100 cursor-pointer"
        >
          <TableCell className="py-3 px-6">
            <Link
              to={`/case/${caseItem.id}?user_id=${caseItem.user_id}`}
              className="text-blue-500 hover:underline"
            >
              {caseItem.id}
            </Link>
          </TableCell>
          <TableCell className="py-3 px-6">{caseItem.user_id}</TableCell>
          <TableCell className="py-3 px-6">{formatDate(caseItem.testdate)}</TableCell>
          <TableCell className="py-3 px-6">{caseItem.sorethroat ? 'Yes' : 'No'}</TableCell>
          <TableCell className="py-3 px-6">{caseItem.fever ? 'Yes' : 'No'}</TableCell>
          <TableCell className="py-3 px-6">{caseItem.headache ? 'Yes' : 'No'}</TableCell>
          <TableCell className="py-3 px-6">{caseItem.abdominalpain ? 'Yes' : 'No'}</TableCell>
          <TableCell className="py-3 px-6">{caseItem.difficultyswallowing ? 'Yes' : 'No'}</TableCell>
          <TableCell className="py-3 px-6">{caseItem.cough ? 'Yes' : 'No'}</TableCell>
          <TableCell className="py-3 px-6">{caseItem.congestion ? 'Yes' : 'No'}</TableCell>
          <TableCell className="py-3 px-6">{caseItem.rhinorrhea ? 'Yes' : 'No'}</TableCell>
          <TableCell className="py-3 px-6">{caseItem.hoarseness ? 'Yes' : 'No'}</TableCell>
          <TableCell className="py-3 px-6">{caseItem.conjunctivitis ? 'Yes' : 'No'}</TableCell>
          <TableCell className="py-3 px-6">{caseItem.knownrecentcontact ? 'Yes' : 'No'}</TableCell>
          <TableCell className="py-3 px-6">{caseItem.historyoftonsillectomy ? 'Yes' : 'No'}</TableCell>
          <TableCell className="py-3 px-6">{caseItem.antibioticsinlast48hours ? 'Yes' : 'No'}</TableCell>
        </TableRow>
      ));
  };

  return (
    <div className="container mx-auto p-8">
      <Typography
        variant="h4"
        component="h1"
        align="center"
        gutterBottom
      >
        Patient Information
      </Typography>
      <Paper className="rounded-lg shadow-lg">
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                {Object.keys(columnHeaderMapping).map((column) => (
                  <TableCell
                    key={column}
                    className="bg-blue-500 text-white font-bold py-3 px-6"
                    sortDirection={
                      sortConfig.key === column
                        ? sortConfig.direction
                        : false
                    }
                  >
                    <TableSortLabel
                      active={sortConfig.key === column}
                      direction={
                        sortConfig.key === column
                          ? sortConfig.direction
                          : 'asc'
                      }
                      onClick={() => handleSort(column)}
                      className="text-white"
                    >
                      {columnHeaderMapping[column]}
                    </TableSortLabel>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>{renderTableData()}</TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={pendingCases.length}
          rowsPerPage={rowsPerPage}
          page={currentPage}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          className="mt-4"
        />
      </Paper>
    </div>
  );
};

export default PatientInformation;
